const REDACTED = '[redacted]';
const INSTALLED = Symbol.for('affinityai.browserSafeConsoleInstalled');
const SAFE_EVENT = /^[a-z][a-z0-9_.:-]{1,100}$/;
const SAFE_KEY =
  /^(?:attempt|category|code|count|decision|durationMs|errorCategory|event|finishReason|flow|handler|has[A-Z_].*|is[A-Z_].*|level|maxTokens|method|model|name|ok|operation|outputLength|phase|reason|retry|route|source|status|step|success|type|validatorCode|version)$/;
const SAFE_SUFFIX = /(?:Count|Length|DurationMs)$/;
const SENSITIVE_KEY =
  /authorization|cookie|credential|secret|token|password|privateKey|apiKey|prompt|message|text|content|body|request|response|raw|summary|scenario|answer|contribution|email|recipient|invitation|header|env|stack|query|url/i;
const IDENTIFIER_KEY = /^(?:instance|company|project|teamMember|member|participant|user|uid)Id$/i;
const SECRET_VALUE =
  /(?:-----BEGIN [A-Z ]*PRIVATE KEY-----|\bBearer\s+\S+|\beyJ[A-Za-z0-9_-]{8,}\.|\bsk-[A-Za-z0-9_-]{12,}|[?&](?:code|state|token|key|secret)=)/i;
const EMAIL_VALUE = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i;

function sanitizeRoute(value) {
  if (typeof value !== 'string') return REDACTED;
  const route = value.split('?')[0].split('#')[0];
  if (SAFE_EVENT.test(route)) return route;
  return route
    .replace(/\/(t|short-links)\/[^/]+/gi, '/$1/[redacted]')
    .replace(/\/(companies|instances?|projects|teamMembers|members)\/[^/]+/gi, '/$1/[redacted]')
    .replace(/\/participants\/[^/]+/gi, '/participants/[redacted]');
}

function safeLabel(value) {
  if (SECRET_VALUE.test(value) || EMAIL_VALUE.test(value)) return REDACTED;
  const bracketed = value.match(/^\[[A-Za-z0-9_.:-]{1,80}\]/);
  if (bracketed) return bracketed[0];
  return SAFE_EVENT.test(value) ? value : REDACTED;
}

export function sanitizeBrowserLogValue(value, depth = 0, seen = new WeakSet()) {
  if (value == null || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    return SAFE_EVENT.test(value) && !SECRET_VALUE.test(value) && !EMAIL_VALUE.test(value)
      ? value
      : REDACTED;
  }
  if (value instanceof Error) {
    const code =
      typeof value.code === 'string' && SAFE_EVENT.test(value.code) ? value.code : 'client_error';
    return { name: value.name || 'Error', code };
  }
  if (typeof value !== 'object' || depth >= 4) return REDACTED;
  if (seen.has(value)) return '[circular]';
  seen.add(value);
  if (Array.isArray(value)) {
    return value.slice(0, 20).map((item) => sanitizeBrowserLogValue(item, depth + 1, seen));
  }
  return Object.fromEntries(
    Object.entries(value)
      .slice(0, 40)
      .map(([key, item]) => {
        if (key === 'route') return [key, sanitizeRoute(item)];
        if (SAFE_SUFFIX.test(key)) {
          return [key, sanitizeBrowserLogValue(item, depth + 1, seen)];
        }
        if (SENSITIVE_KEY.test(key) || IDENTIFIER_KEY.test(key) || !SAFE_KEY.test(key)) {
          return [key, REDACTED];
        }
        return [key, sanitizeBrowserLogValue(item, depth + 1, seen)];
      }),
  );
}

export function installBrowserSafeConsole() {
  if (globalThis[INSTALLED] || typeof console === 'undefined') return;
  globalThis[INSTALLED] = true;
  ['debug', 'log', 'info', 'warn', 'error'].forEach((method) => {
    const original = console[method]?.bind(console);
    if (!original) return;
    console[method] = (...args) =>
      original(
        ...args.map((arg, index) => {
          if (index === 0 && typeof arg === 'string') return safeLabel(arg);
          if (typeof arg === 'string') return SAFE_EVENT.test(arg) ? arg : REDACTED;
          return sanitizeBrowserLogValue(arg);
        }),
      );
  });
}

if (process.env.NODE_ENV !== 'test') installBrowserSafeConsole();
